import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Footer as SimbaFooter } from '@naf-simba/sections-footer';
import type { FooterInternalLink } from '@naf-simba/sections-footer';
import { useAuth0 } from '@auth0/auth0-react';
import { MEMBERSHIP } from '@lib/constants';
import { resolveDocumentUrl } from '../../hooks/Documents/useDocumentUrl';
import type { DocumentData } from '../../hooks/Documents/useDocumentUrl.types';
import { actions } from '../../redux/modules/globalSettings';
import useSelector from '../../redux/typedHooks';
import { useTrackCTA } from '../../utils/Tracking/CtaClick/TrackCtaClick';
import { useWindowLocation } from '../../hooks/Window/useWindowLocation';
import { useTrackPromotion } from '../../hooks/useTrackPromotion';
import { shouldHideBecomeMemberButton } from '../../utils/hideBecomeMemberButton';
import * as S from './Footer.styles';

const CTA_TEXT = 'Bli medlem';
const ORGANISATION = 'Norges Automobil-Forbund';

const Footer = () => {
  const { isAuthenticated } = useAuth0();
  const isMember = useSelector((state) => state?.myMembership?.customerInformation?.data?.membership?.isValid ?? false);
  const footerInfo = useSelector((state) => state.globalSettings.settings?.footer);
  const { mappedCategories } = useSelector((state) => state.sitestructure);
  const trackCTA = useTrackCTA();
  const currentUrl = useWindowLocation();
  const hideBecomeMemberButton = shouldHideBecomeMemberButton(currentUrl, { isMember, isAuthenticated });

  // The DS exposes the call to action as button props only, with nowhere to hang a ref, so the
  // promotion is observed on the footer around it — it sits at the top, so the two come into view
  // together.
  const { wrapperRef, trackSelectPromotion } = useTrackPromotion({
    creative_slot: 'footer',
    creative_name: `CTA | secondaryLight | ${CTA_TEXT}`,
  });

  const dispatch = useDispatch();
  useEffect(() => {
    if (typeof footerInfo === 'undefined' || (footerInfo && Object.keys(footerInfo).length === 0)) {
      dispatch(actions.getGlobalSettings.request(''));
    }
  }, [dispatch, footerInfo]);

  // Resolving a reference needs the site structure, which is the app's to know, so the DS asks for
  // it rather than guessing. The cast is our resolver's, not the footer's: the CMS types a
  // reference's `type` as a string, and `resolveDocumentUrl` expects the document-type enum.
  const resolveTo = useCallback(
    (link: FooterInternalLink) => resolveDocumentUrl(link.reference as DocumentData, mappedCategories) ?? '',
    [mappedCategories],
  );

  const handleTrackMembershipClick = () => {
    trackSelectPromotion();
    trackCTA({
      cta_position: 'footer',
      text: CTA_TEXT,
      url: MEMBERSHIP,
    });
  };

  return (
    <S.FooterOuter ref={wrapperRef} data-elastic-exclude>
      <SimbaFooter
        data={footerInfo}
        resolveTo={resolveTo}
        logoHref="/"
        organisation={ORGANISATION}
        copyright={`© ${ORGANISATION}`}
        isMember={hideBecomeMemberButton}
        action={{ children: CTA_TEXT, to: MEMBERSHIP, onClick: handleTrackMembershipClick }}
      />
    </S.FooterOuter>
  );
};

export default Footer;
